import { useT } from "../../i18n/I18nContext";

// Shared locale filter chip strip — surfaced on Browse Catalogue,
// Search, and any other discovery surface that aggregates content
// across both EN and FR. Default selection is the viewer's current
// UI locale ("show me content I can actually read"); the other
// chips let the user expand to the other locale or see all.
//
// The Dashboard (Collaboration.jsx) intentionally does NOT mount
// this — it silently filters to current locale so the dashboard
// stays a quick-look surface without filter chrome.
//
// Counts (optional) render after each label so the user sees the
// scope before clicking ("FR · 12"). The backend ships counts on
// the same response that drives the list, so they're free.
//
// Props:
//   currentLocale : 'fr' | 'en' — the UI locale, used to label the
//                   primary chip and decide which is "other".
//   value         : 'fr' | 'en' | 'all' — selected state.
//   onChange(v)   : fired when the writer picks a chip.
//   counts        : { fr, en, all } | null — optional badge counts.
export default function LocaleFilterChips({
  currentLocale = "en",
  value = "en",
  onChange,
  counts = null,
  className = "",
  // ``compact`` shrinks the chips for placement inline with other
  // toolbar controls (search bar, sort dropdown). Used on Browse
  // so the filter doesn't claim its own row.
  compact = false,
}) {
  const t = useT();
  const current = currentLocale === "fr" ? "fr" : "en";
  const other = current === "fr" ? "en" : "fr";

  const chips = [
    {
      id: current,
      label: t(`locale_filter.${current}`),
      count: counts?.[current],
    },
    {
      id: other,
      label: t(`locale_filter.${other}`),
      count: counts?.[other],
    },
    {
      id: "all",
      label: t("locale_filter.all"),
      count: counts?.all,
    },
  ];

  const chipPad = compact ? "px-2 py-1 text-[10px]" : "px-3 py-1 text-[11px]";
  return (
    <div
      role="group"
      aria-label={t("locale_filter.aria")}
      className={`inline-flex rounded-full border border-border bg-surface overflow-hidden ${className}`}
    >
      {chips.map((chip) => {
        const active = value === chip.id;
        return (
          <button
            key={chip.id}
            type="button"
            onClick={() => onChange?.(chip.id)}
            aria-pressed={active}
            className={[
              chipPad,
              "inline-flex items-center gap-1 transition tabular-nums",
              active
                ? "bg-primaryMuted/60 text-text font-medium"
                : "text-secondText hover:bg-surfaceAlt hover:text-text",
            ].join(" ")}
          >
            <span>{chip.label}</span>
            {typeof chip.count === "number" ? (
              <span className="text-[10px] opacity-70">· {chip.count}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
